import React, { useEffect, useRef, useState } from 'react';
import type { UseMonitoringPage } from '../../useMonitoringPage';
import classes from '../shared/filter-bar.module.scss';

type Filters = UseMonitoringPage['mergeFilters'];
type FilterKey = keyof Filters;

const TEXT_FIELDS: { key: FilterKey; label: string }[] = [
  { key: 'sessionName', label: 'הפעלה' },
  { key: 'performedByName', label: 'מלצר' },
  { key: 'tableName', label: 'שולחן' },
];

const KIND_OPTIONS = [
  { value: 'merge', label: 'מיזוג' },
  { value: 'partial_split', label: 'פיצול חלקי' },
  { value: 'full_split', label: 'פיצול מלא' },
];

type Props = Pick<UseMonitoringPage, 'mergeFilters' | 'setMergeFilters' | 'clearMergeFilters'>;

export const MergeFilters: React.FC<Props> = ({ mergeFilters, setMergeFilters, clearMergeFilters }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [scopedKeys, setScopedKeys] = useState<FilterKey[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scopedKeys.length === 0) {
      const update: Partial<Filters> = { search: searchText };
      TEXT_FIELDS.forEach(({ key }) => { update[key] = ''; });
      setMergeFilters((prev) => ({ ...prev, ...update }));
    } else {
      const update: Partial<Filters> = { search: '' };
      TEXT_FIELDS.forEach(({ key }) => {
        update[key] = scopedKeys.includes(key) ? searchText : '';
      });
      setMergeFilters((prev) => ({ ...prev, ...update }));
    }
  }, [searchText, scopedKeys, setMergeFilters]);

  const toggleScope = (key: FilterKey) => {
    setScopedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleKindToggle = (value: string) => {
    setMergeFilters((f) => ({ ...f, kind: f.kind === value ? '' : value }));
  };

  const clearAll = () => {
    setScopedKeys([]);
    setSearchText('');
    clearMergeFilters();
  };

  const hasDateFrom = !!mergeFilters.dateFrom;
  const hasDateTo = !!mergeFilters.dateTo;
  const hasActive = scopedKeys.length > 0 || !!mergeFilters.kind || hasDateFrom || hasDateTo;

  return (
    <div className={classes.root}>
      <div className={classes.searchBar}>
        {scopedKeys.map((key) => (
          <span key={key} className={classes.chip}>
            {TEXT_FIELDS.find(f => f.key === key)?.label}
            <button className={classes.chipRemove} type="button" data-testid={`monitoring-merges-chip-remove-scope-${key}`}
              onClick={(e) => { e.stopPropagation(); toggleScope(key); }}>{'×'}</button>
          </span>
        ))}
        {mergeFilters.kind && (
          <span className={classes.chip}>
            {KIND_OPTIONS.find(k => k.value === mergeFilters.kind)?.label}
            <button className={classes.chipRemove} type="button" data-testid="monitoring-merges-chip-remove-kind"
              onClick={(e) => { e.stopPropagation(); handleKindToggle(mergeFilters.kind); }}>{'×'}</button>
          </span>
        )}
        {hasDateFrom && (
          <span className={classes.chip}>
            {'מ־' + mergeFilters.dateFrom}
            <button className={classes.chipRemove} type="button" data-testid="monitoring-merges-chip-remove-date-from"
              onClick={(e) => { e.stopPropagation(); setMergeFilters((f) => ({ ...f, dateFrom: '' })); }}>{'×'}</button>
          </span>
        )}
        {hasDateTo && (
          <span className={classes.chip}>
            {'עד ' + mergeFilters.dateTo}
            <button className={classes.chipRemove} type="button" data-testid="monitoring-merges-chip-remove-date-to"
              onClick={(e) => { e.stopPropagation(); setMergeFilters((f) => ({ ...f, dateTo: '' })); }}>{'×'}</button>
          </span>
        )}
        <input
          ref={inputRef}
          className={classes.input}
          placeholder={!hasActive ? 'חיפוש...' : ''}
          value={searchText}
          onChange={(e) => setSearchText(e.currentTarget.value)}
          data-testid="monitoring-merges-search"
        />
        {hasActive && (
          <button className={classes.clearBtn} type="button" onClick={clearAll} data-testid="monitoring-merges-clear">{'×'}</button>
        )}
        <button
          className={`${classes.filterBtn} ${hasActive ? classes.filterBtnActive : ''}`}
          type="button"
          onClick={() => setDropdownOpen((o) => !o)}
          aria-label="סינון"
          data-testid="monitoring-merges-filter-toggle"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M1 2.5A.5.5 0 0 1 1.5 2h13a.5.5 0 0 1 .354.854L10 8.207V13.5a.5.5 0 0 1-.276.447l-3 1.5A.5.5 0 0 1 6 15V8.207L1.146 2.854A.5.5 0 0 1 1 2.5z"/>
          </svg>
        </button>
      </div>

      {dropdownOpen && (
        <>
          <div className={classes.dropdown}>
            <div className={classes.dropdownSection}>
              <div className={classes.dropdownSectionTitle}>{'שדות חיפוש'}</div>
              {TEXT_FIELDS.map((f) => (
                <div
                  key={f.key}
                  className={`${classes.dropdownItem} ${scopedKeys.includes(f.key) ? classes.active : ''}`}
                  onClick={() => toggleScope(f.key)}
                  data-testid={`monitoring-merges-field-${f.key}`}
                >
                  <span className={classes.checkmark}>{scopedKeys.includes(f.key) ? '✓' : ''}</span>
                  {f.label}
                </div>
              ))}
            </div>
            <div className={classes.dropdownDivider} />
            <div className={classes.dropdownSection}>
              <div className={classes.dropdownSectionTitle}>{'תאריך'}</div>
              <div className={classes.dateRow}>
                <label className={classes.dateLabel}>{'מ־'}</label>
                <input
                  type="date"
                  className={classes.dateInput}
                  value={mergeFilters.dateFrom}
                  onChange={(e) => setMergeFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                  data-testid="monitoring-merges-date-from"
                />
              </div>
              <div className={classes.dateRow}>
                <label className={classes.dateLabel}>{'עד'}</label>
                <input
                  type="date"
                  className={classes.dateInput}
                  value={mergeFilters.dateTo}
                  onChange={(e) => setMergeFilters((f) => ({ ...f, dateTo: e.target.value }))}
                  data-testid="monitoring-merges-date-to"
                />
              </div>
            </div>
            <div className={classes.dropdownDivider} />
            <div className={classes.dropdownSection}>
              <div className={classes.dropdownSectionTitle}>{'סוג'}</div>
              {KIND_OPTIONS.map((k) => (
                <div
                  key={k.value}
                  className={`${classes.dropdownItem} ${mergeFilters.kind === k.value ? classes.active : ''}`}
                  onClick={() => handleKindToggle(k.value)}
                  data-testid={`monitoring-merges-kind-${k.value}`}
                >
                  <span className={classes.checkmark}>{mergeFilters.kind === k.value ? '✓' : ''}</span>
                  {k.label}
                </div>
              ))}
            </div>
          </div>
          <div className={classes.backdrop} onClick={() => setDropdownOpen(false)} />
        </>
      )}
    </div>
  );
};
